import { DataSource } from '@angular/cdk/table';
import { MeteringPointReadingPoint } from '@model';
import { BehaviorSubject, Observable, merge, of } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { map } from 'rxjs/operators';
import dayjs from 'dayjs';

export class MeteringPointMeteringPaneDataSource extends DataSource<MeteringPointReadingPoint> {
  data: MeteringPointReadingPoint[] = [];

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  renderedData: MeteringPointReadingPoint[] = [];

  constructor(private paginator: MatPaginator, private sort: MatSort, private staticData: MeteringPointReadingPoint[]) {
    super();
    this.loadingSubject.next(true);

    of(this.staticData).subscribe(reads => {
      this.data = reads;
      this.data.length > 0 ? this.loadingSubject.next(false) : this.loadingSubject.next(true);
    });
  }

  connect(): Observable<MeteringPointReadingPoint[]> {
    // Set the paginators length
    this.paginator.length = this.data.length;
    
    return merge(of(this.staticData), this.paginator.page, this.sort.sortChange).pipe(
      map(() => {
        // Sort filtered data
        const sortedData = this.getSortedData(this.data.slice());

        // Grab the page's slice of the filtered sorted data.
        const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
        this.renderedData = sortedData.splice(startIndex, this.paginator.pageSize);
        return this.renderedData;
      })
    );
  }

  /**
   * Sort the data (client-side). If you're using server-side sorting,
   * this would be replaced by requesting the appropriate data from the server.
   */
  private getSortedData(data: MeteringPointReadingPoint[]) {
    if (!this.sort.active || this.sort.direction === '') {
      return data;
    }

    return data.sort((a, b) => {
      const isAsc = this.sort.direction === 'asc';
      switch (this.sort.active) {
        case 'timestamp':
          return this.compare(+dayjs(a.timeStamp), +dayjs(b.timeStamp), isAsc);
        case 'quantity':
          return this.compare(a.quantity, b.quantity, isAsc);
        case 'statusName':
          return this.compare(a.statusName, b.statusName, isAsc);
        default:
          return 0;
      }
    });
  }

  disconnect() {}

  compare(a, b, isAsc) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }
}
